import Icon from '../../../components/AppIcon';
import { RegulatoryChange } from '../types';

interface HistoricalTimelineProps {
  changes: RegulatoryChange[];
  onChangeClick?: (change: RegulatoryChange) => void;
}

const HistoricalTimeline = ({ changes, onChangeClick }: HistoricalTimelineProps) => {
  const getImpactColor = (level: string): string => {
    switch (level) {
      case 'high':
        return 'bg-error text-error-foreground';
      case 'medium':
        return 'bg-warning text-warning-foreground';
      case 'low':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'rate_change':
        return 'TrendingUp';
      case 'exemption_added':
        return 'CheckCircle2';
      case 'regulation_updated':
        return 'FileText';
      case 'classification_changed':
        return 'Tag';
      default:
        return 'Info';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'rate_change':
        return 'Alteração de Alíquota';
      case 'exemption_added':
        return 'Isenção Adicionada';
      case 'regulation_updated':
        return 'Regulamentação Atualizada';
      case 'classification_changed':
        return 'Classificação Alterada';
      default:
        return 'Alteração';
    }
  };

  const sortedChanges = [...changes].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">Histórico de Alterações</h3>

      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>

        <div className="space-y-6">
          {sortedChanges.map((change) => (
            <div
              key={change.id}
              onClick={() => onChangeClick?.(change)}
              className="relative pl-12 cursor-pointer group"
            >
              <div className="absolute left-0 w-8 h-8 rounded-full bg-card border-2 border-primary flex items-center justify-center group-hover:scale-110 transition-transform">
                <Icon name={getTypeIcon(change.type)} size={16} color="var(--color-primary)" />
              </div>

              <div className="bg-card border border-border rounded-lg p-4 group-hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-foreground">
                        {getTypeLabel(change.type)}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getImpactColor(change.impactLevel)}`}>
                        {change.impactLevel === 'high' && 'Alto Impacto'}
                        {change.impactLevel === 'medium' && 'Médio Impacto'}
                        {change.impactLevel === 'low' && 'Baixo Impacto'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(change.date).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-foreground mb-3">{change.description}</p>

                {change.previousValue && (
                  <div className="flex items-center gap-4 mb-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Anterior:</span>
                      <span className="font-mono text-error line-through">{change.previousValue}</span>
                    </div>
                    <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Novo:</span>
                      <span className="font-mono text-success font-semibold">{change.newValue}</span>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2 p-3 bg-muted rounded-md">
                  <Icon name="Scale" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Base Regulatória</p>
                    <p className="text-sm text-foreground">{change.regulatoryBasis}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {sortedChanges.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Clock" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhuma alteração histórica registrada</p>
        </div>
      )}
    </div>
  );
};

export default HistoricalTimeline;